import { EmptyStateIcons, EmptyStateProps, SortDirection, Table } from '@silverfort/sf-components';
import { ServiceAccount } from '../ServiceAccountsTypes';
import { FC, SVGProps, useCallback, useMemo } from 'react';
import buildSAColumns from '../components/ServiceAccountColumns';
import { useServiceAccountContext } from '../contexts/ServiceAccountContext';
import { INFINITE_SCROLL_BATCH_SIZE, SERVICE_ACCOUNT_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { Pages, Roles } from '../../../../context/contexts/UserContext/permissionsTypes';
import useHasPermissions from '../../../../hooks/useHasPermissions';

const EMPTY_STATE: EmptyStateProps = {
    title: 'No accounts',
    icon: EmptyStateIcons.NoItems as FC<SVGProps<SVGSVGElement>>,
    className: 'pt-6 pb-12 px-8 max-h-[236px] min-h-[236px]'
};

const ServiceAccountsTable: FC<{ tableHeight: number | undefined }> = ({ tableHeight }) => {
    const {
        serviceAccounts,
        lastFetchLength,
        fetchServiceAccounts,
        selectServiceAccount,
        orderBy,
        setOrderBy,
        isFetchingServiceAccounts,
        tabs,
        activeTab,
        setSelectedIds,
        onPremNHILicenseISPMEnabled,
        onPremNHILicenseITDREnabled,
        onPremNHILicenseProtectionEnabled
    } = useServiceAccountContext();

    const isEditor = useHasPermissions({ permission: Pages.SERVICE_ACCOUNTS, role: Roles.EDITOR });

    const columns = useMemo(() => {
        const cols = buildSAColumns(
            selectServiceAccount,
            onPremNHILicenseISPMEnabled || onPremNHILicenseITDREnabled,
            onPremNHILicenseProtectionEnabled
        );
        return cols.map((column) => {
            if (column.columnKey === 'calculated_name') {
                return {
                    ...column,
                    title: `Name (${serviceAccounts.length.toLocaleString()} / ${tabs[
                        activeTab
                    ]?.amount.toLocaleString()})`
                };
            }
            return column;
        });
    }, [
        activeTab,
        onPremNHILicenseISPMEnabled,
        onPremNHILicenseITDREnabled,
        onPremNHILicenseProtectionEnabled,
        selectServiceAccount,
        serviceAccounts.length,
        tabs
    ]);

    const onSortChange = useCallback(
        (column: keyof ServiceAccount, direction: SortDirection) => {
            setOrderBy({ column, direction });
        },
        [setOrderBy]
    );

    const onsScrollEnd = useCallback(() => {
        if (lastFetchLength < INFINITE_SCROLL_BATCH_SIZE) {
            return;
        }
        if (!isFetchingServiceAccounts) {
            fetchServiceAccounts(serviceAccounts.length);
        }
    }, [serviceAccounts, lastFetchLength, fetchServiceAccounts, isFetchingServiceAccounts]);

    const selectedIds = useMemo(() => {
        return tabs[activeTab].selectedServiceAccounts.map((sa) => sa.id);
    }, [activeTab, tabs]);

    return (
        <Table
            getRowCheckboxProps={(sa) =>
                sa.is_internal_sa
                    ? { disabled: true, tooltipTitle: 'Bulk action not supported for Silverfort service account' }
                    : { disabled: false, tooltipTitle: '' }
            }
            columns={columns}
            rows={serviceAccounts}
            loading={isFetchingServiceAccounts}
            selectable={isEditor && activeTab !== SERVICE_ACCOUNT_TYPES.REMOVED.type}
            keyExtractor={(data) => data?.id}
            selected={selectedIds}
            onSelect={setSelectedIds}
            defaultSortColumn={orderBy.column}
            defaultSortDirection={orderBy.direction}
            onSortChange={onSortChange}
            tableContainerClassName="w-full h-[calc(100vh-450px)]"
            bodyHeight={tableHeight ? tableHeight - 58 : 0}
            EmptyStateProps={EMPTY_STATE}
            onScrollEnd={onsScrollEnd}
            menuItems={[
                {
                    title: 'Manage Account',
                    onClick: (account?: ServiceAccount) => selectServiceAccount(account?.guid)
                }
            ]}
            isHorizontalScrollAvailable={true}
        />
    );
};

export default ServiceAccountsTable;
